import { useState } from "react";
import { toast } from "sonner";
import { PageHero, SectionHeading } from "@/components/site/Sections";
import { Wizard } from "@/components/site/Wizard";
import {
  StepGrid, StepTitle, Text, Money, Num, Select, Area, Chips, MultiChips, UploadStub,
} from "@/components/site/WizardControls";
import { submitBuyer } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { CheckCircle2 } from "lucide-react";

const INV_TYPE = ["Fix & Flip Investor", "Buy & Hold Investor", "BRRRR Investor", "Landlord", "Multifamily Investor", "Developer", "Commercial Investor", "Land Investor", "Wholesaler", "Other"];
const EXP = ["First Property", "1–3 Deals", "4–10 Deals", "11–25 Deals", "25+ Deals"];
const STATES = ["Pennsylvania"];
const PROP_TYPES = ["Single Family", "Duplex", "Triplex", "Fourplex", "Multifamily 5+", "Condo", "Townhouse", "Mobile Home", "Mixed Use", "Commercial", "Land", "Other"];
const REHAB = ["Turnkey / No Rehab", "Light Cosmetic", "Medium Rehab", "Heavy Rehab", "Full Gut", "Structural / Major Project"];
const REHAB_BUDGET = ["Under $10,000", "$10,000–$25,000", "$25,000–$50,000", "$50,000–$75,000", "$75,000–$100,000", "$100,000+", "No Fixed Limit", "Depends on the Deal"];
const STRATEGY = ["Fix & Flip", "Buy & Hold", "BRRRR", "Long-Term Rental", "Short-Term Rental", "Wholesale", "Multifamily", "Development", "Land", "Other"];
const OCC_PREF = ["Vacant Properties", "Tenant Occupied", "Owner Occupied", "Property Requiring Possession", "Property Requiring Eviction Coordination", "Auction Properties", "Tax Sale Properties", "Sheriff Sale Properties", "Foreclosures", "Off-Market Properties", "Wholesale Assignments", "Distressed Properties"];
const FINANCING = ["Cash", "Hard Money", "DSCR", "Fix & Flip Financing", "Bridge Financing", "Private Money", "Conventional", "Not Sure", "Need Financing Assistance"];
const POF = ["Yes", "No", "In Process"];
const CLOSE = ["3–7 Days", "7–14 Days", "15–30 Days", "30+ Days", "Depends on Financing"];
const VOLUME = ["One", "1–3 Per Year", "4–10 Per Year", "10+ Per Year", "As Many Good Deals as Possible"];
const ALERTS = ["Email", "SMS", "Phone", "Client Portal"];
const FREQ = ["Immediately", "Daily Summary", "Weekly Summary"];

const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function Buy() {
  const [f, setF] = useState({ states: [], property_types: [], rehab_levels: [], occupancy_prefs: [], alert_methods: [], wants_financing: "Maybe", proof_of_funds: "In Process", consider_outside: "Maybe" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const onSubmit = async () => {
    if (!f.first_name || !f.last_name || !f.phone || !f.email || !f.max_purchase_price) {
      toast.error("Please complete contact details and maximum purchase price.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...f, counties: toArr(f.counties_text), cities: toArr(f.cities_text) };
      await submitBuyer(payload);
      setDone(true);
      toast.success("Your Buy Box has been created.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const isFlip = f.primary_strategy === "Fix & Flip";
  const isHold = ["Buy & Hold", "BRRRR", "Long-Term Rental"].includes(f.primary_strategy);

  const steps = [
    { key: "Investor", title: "Investor Information", render: () => (
      <StepGrid>
        <StepTitle>About You</StepTitle>
        <Text label="First Name" value={f.first_name} onChange={set("first_name")} required testid="buy-first-name" />
        <Text label="Last Name" value={f.last_name} onChange={set("last_name")} required testid="buy-last-name" />
        <Text label="Company" value={f.company} onChange={set("company")} />
        <Text label="Phone" type="tel" value={f.phone} onChange={set("phone")} required testid="buy-phone" />
        <Text label="Email" type="email" value={f.email} onChange={set("email")} required testid="buy-email" />
        <Select label="Investor Type" value={f.investor_type} onChange={set("investor_type")} options={INV_TYPE} testid="buy-investor-type" />
        <Select label="Investor Experience" value={f.experience} onChange={set("experience")} options={EXP} />
      </StepGrid>
    )},
    { key: "Location", title: "Target Location", render: () => (
      <StepGrid>
        <StepTitle hint="Where do you want to buy?">Target Markets</StepTitle>
        <MultiChips label="States" options={STATES} values={f.states} onChange={set("states")} testid="buy-states" />
        <Text label="Counties (comma separated)" value={f.counties_text} onChange={set("counties_text")} full testid="buy-counties" />
        <Text label="Cities (comma separated)" value={f.cities_text} onChange={set("cities_text")} full />
        <Text label="ZIP Codes (optional)" value={f.zip_codes} onChange={set("zip_codes")} />
        <Text label="Radius (optional)" value={f.radius} onChange={set("radius")} />
        <Chips label="Consider properties outside your area if the deal is strong?" options={["Yes", "No", "Maybe"]} value={f.consider_outside} onChange={set("consider_outside")} />
      </StepGrid>
    )},
    { key: "Property", title: "Property Type", render: () => (
      <StepGrid>
        <StepTitle>What are you looking for?</StepTitle>
        <MultiChips label="Property types" options={PROP_TYPES} values={f.property_types} onChange={set("property_types")} testid="buy-prop-types" />
        <Num label="Min Bedrooms" value={f.beds_min} onChange={set("beds_min")} />
        <Num label="Max Bedrooms" value={f.beds_max} onChange={set("beds_max")} />
        <Num label="Min Bathrooms" value={f.baths_min} onChange={set("baths_min")} />
        <Num label="Max Bathrooms (optional)" value={f.baths_max} onChange={set("baths_max")} />
        <Num label="Min Square Feet" value={f.sqft_min} onChange={set("sqft_min")} />
        <Num label="Max Square Feet" value={f.sqft_max} onChange={set("sqft_max")} />
        <Text label="Lot Size (optional)" value={f.lot_size} onChange={set("lot_size")} />
      </StepGrid>
    )},
    { key: "Price", title: "Price", render: () => (
      <StepGrid>
        <StepTitle>What is your maximum purchase price?</StepTitle>
        <Money label="Minimum Purchase Price" value={f.min_purchase_price} onChange={set("min_purchase_price")} optional />
        <Money label="Target Purchase Price" value={f.target_purchase_price} onChange={set("target_purchase_price")} optional />
        <div className="sm:col-span-2"><Money label="Maximum Purchase Price *" value={f.max_purchase_price} onChange={set("max_purchase_price")} testid="buy-max-price" /></div>
        <Money label="Minimum ARV" value={f.min_arv} onChange={set("min_arv")} optional />
        <Money label="Maximum ARV" value={f.max_arv} onChange={set("max_arv")} optional />
        <Money label="Max Total Project Cost" value={f.max_project_cost} onChange={set("max_project_cost")} optional />
        <Money label="Max Cash Needed at Closing" value={f.max_cash} onChange={set("max_cash")} optional />
      </StepGrid>
    )},
    { key: "Rehab", title: "Rehab Preference", render: () => (
      <StepGrid>
        <StepTitle>What renovation level are you comfortable with?</StepTitle>
        <MultiChips label="Acceptable rehab levels" options={REHAB} values={f.rehab_levels} onChange={set("rehab_levels")} testid="buy-rehab-levels" />
        <Chips label="Maximum rehab budget" options={REHAB_BUDGET} value={f.rehab_budget} onChange={set("rehab_budget")} />
      </StepGrid>
    )},
    { key: "Strategy", title: "Investment Strategy", render: () => (
      <StepGrid>
        <StepTitle>Your strategy</StepTitle>
        <Chips label="Primary strategy" options={STRATEGY} value={f.primary_strategy} onChange={set("primary_strategy")} testid="buy-strategy" />
        <Select label="Secondary strategy (optional)" value={f.secondary_strategy} onChange={set("secondary_strategy")} options={STRATEGY} />
        {isFlip && (
          <>
            <Money label="Minimum Desired Profit" value={f.min_profit} onChange={set("min_profit")} />
            <Num label="Minimum ROI (%)" value={f.min_roi} onChange={set("min_roi")} />
            <Money label="Maximum Rehab" value={f.max_rehab} onChange={set("max_rehab")} />
            <Text label="Target Project Timeline" value={f.project_timeline} onChange={set("project_timeline")} />
          </>
        )}
        {isHold && (
          <>
            <Money label="Minimum Monthly Cash Flow" value={f.min_cashflow} onChange={set("min_cashflow")} />
            <Money label="Target Monthly Rent" value={f.target_rent} onChange={set("target_rent")} />
            <Num label="Minimum Cap Rate (%)" value={f.min_cap} onChange={set("min_cap")} />
            <Num label="Minimum DSCR" value={f.min_dscr} onChange={set("min_dscr")} />
          </>
        )}
        <MultiChips label="Willing to buy (deal types)" options={OCC_PREF} values={f.occupancy_prefs} onChange={set("occupancy_prefs")} testid="buy-occupancy" />
      </StepGrid>
    )},
    { key: "Financing", title: "Financing", render: () => (
      <StepGrid>
        <StepTitle>How will you purchase?</StepTitle>
        <Chips label="Financing method" options={FINANCING} value={f.financing_type} onChange={set("financing_type")} testid="buy-financing" />
        <Chips label="Proof of Funds?" options={POF} value={f.proof_of_funds} onChange={set("proof_of_funds")} />
        <UploadStub label="Upload Proof of Funds (optional)" testid="buy-pof-file" onNote={() => toast.info("Noted. A coordinator will request a secure upload.")} />
        <Chips label="Want Vertexhub to help you explore financing options?" options={["Yes", "No", "Maybe"]} value={f.wants_financing} onChange={set("wants_financing")} testid="buy-wants-financing" />
      </StepGrid>
    )},
    { key: "Closing", title: "Closing Speed", render: () => (
      <StepGrid>
        <StepTitle>Speed & volume</StepTitle>
        <Chips label="How quickly can you close?" options={CLOSE} value={f.closing_speed} onChange={set("closing_speed")} />
        <Chips label="How many properties are you looking to buy?" options={VOLUME} value={f.purchase_volume} onChange={set("purchase_volume")} />
      </StepGrid>
    )},
    { key: "Alerts", title: "Deal Alerts", render: () => (
      <StepGrid>
        <StepTitle>How should we send matching opportunities?</StepTitle>
        <MultiChips label="Alert methods" options={ALERTS} values={f.alert_methods} onChange={set("alert_methods")} />
        <Chips label="Frequency" options={FREQ} value={f.alert_frequency} onChange={set("alert_frequency")} />
        <Area label="Anything else about your Buy Box?" value={f.notes} onChange={set("notes")} />
      </StepGrid>
    )},
  ];

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Created" title="Buy Box Created." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              Your acquisition criteria have been saved. Vertexhub Ventures will use your Buy Box to identify and
              prioritize opportunities that may fit your investment strategy. Property availability is not guaranteed.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="I Want to Buy"
        title="I Want to Buy a Property."
        subtitle="Tell us where you want to buy, what types of properties you're looking for, your maximum purchase price, renovation tolerance, financing, and investment strategy."
        image={IMAGES.interior}
      />
      <section className="border-t border-slate-200 bg-[#F5F3EC] py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <SectionHeading center eyebrow="Acquisition Criteria" title="Create My Buy Box" light={false} />
          <div className="mt-10">
            <Wizard steps={steps} onSubmit={onSubmit} submitting={sending} submitLabel="Create My Buy Box" testid="buyer-form" />
          </div>
        </div>
      </section>
    </div>
  );
}
